import React from 'react';
import { useTranslation } from 'react-i18next';

import { ModerationHistory } from '../../../../../../models';
import useDateFormat from '../../../../../../components/utils/useDateFormat';

import { Card, ContentCard, EmptyState, HeaderCard, Icon, SubCard, SubTitleCard, TitleCard, Status, ToastContext } from '@weezevent/nacre';

import { ActionLogsList, SpamLogEntry } from './ActionLogs/ActionLogs';

import css from '../../moderationProfile.module.css';

export default function ModerationProfileHistory({ organizationId, flowId, attendee, historyDatas, eventLogsHistoryDatas, loadingHistoryDatas, syncHistory }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const context = attendee.event_spam_moderation ? 'event' : 'attendee';

    const sortedAndMergedAttendeeAndEventLogs = React.useMemo(() => {
        if ((!historyDatas && !eventLogsHistoryDatas) || loadingHistoryDatas) {
            return [];
        }
        const merged = [...(historyDatas || []), ...(eventLogsHistoryDatas || [])];
        return merged.sort((a, b) => b.created.localeCompare(a.created));
    }, [historyDatas, eventLogsHistoryDatas, loadingHistoryDatas]);

    const handleAction = React.useCallback(
        ({ method, id, data }) => {
            ModerationHistory[method]({
                organizationId,
                flowId,
                id,
                data
            })
                .then(() => {
                    toast.success(t('ticket.moderation.attendee.history.action.success'));
                    syncHistory();
                })
                .catch(() => {
                    toast.error(t('ticket.moderation.attendee.history.action.error'));
                });
        },
        [organizationId, flowId, syncHistory]
    );

    return (
        <Card id="history" className={`${css['attendee-card']} ${css['attendee-history']}`}>
            <HeaderCard>
                <TitleCard title={t('ticket.moderation.attendee.history.title')} />
                <SubTitleCard title={t(`ticket.moderation.attendee.history.subtitle_${context}`)} />
            </HeaderCard>
            <ContentCard>
                {!historyDatas?.length && !loadingHistoryDatas ? (
                    <EmptyState icon={<Icon margin name="history" />} title={t('ticket.moderation.attendee.history.emptyStateTitle')} />
                ) : (
                    sortedAndMergedAttendeeAndEventLogs.map((historyData, index) => (
                        <SubCardHistory
                            key={historyData.id}
                            created={historyData.created}
                            full_name={historyData.name}
                            event_timezone={attendee.event_timezone}
                            color={historyData.color}
                            statusName={historyData.status}
                        >
                            {historyData.isSpamLog ? (
                                <SpamLogEntry historyData={historyData} />
                            ) : (
                                <ActionLogsList historyData={historyData} datas={historyDatas} handleAction={handleAction} attendee={attendee} />
                            )}
                        </SubCardHistory>
                    ))
                )}
            </ContentCard>
        </Card>
    );
}

const SubCardHistory = ({ created, full_name, event_timezone, text, color, statusName, children }) => {
    const [, formattedTime, formattedDate] = useDateFormat({
        date: created,
        tz: event_timezone,
        datecss: 'short',
        timecss: 'short'
    });
    const isRevertAction = React.useMemo(() => {
        return text === 'Revert action';
    }, [text]);

    return (
        <SubCard
            className={css['attendee-history-subCard']}
            title={
                <div className={css['attendee-history-subCardTitle']}>
                    <div>{`${formattedDate} - ${formattedTime}`}</div>
                    <span>{full_name}</span>
                </div>
            }
        >
            <div className={css['attendee-history-statuses']}>{!isRevertAction && <Status color={color} text={statusName} />}</div>
            {children}
        </SubCard>
    );
};
